import { Briefcase, Check, ChevronRight, Heart } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Avatar, Badge, Button, Card, PageHeader, SectionLabel } from '../components/ui';
import { billsAPI, billTrackingAPI } from '../lib/api';
import { useCitizenWorkspaceId } from '../hooks/useCitizenData';
import { fmtDateShort, initialsOf, partyColor } from '../lib/utils';
import { BILL_STAGES as STAGES, deriveStage, flipName } from '../lib/billStage';
import { formatPartyDisplay } from '../../utils/partyUtils';

interface BillDetail {
  bill: {
    id: string;
    number: string;
    title: string;
    concerning?: string | null;
    status?: string | null;
    chamber?: string | null;
    latest_action?: string | null;
    latest_location?: string | null;
    introduced_date?: string | null;
    subject?: string[];
  };
  sponsors: Array<{
    sponsor_type?: string;
    legislator_id?: string;
    name?: string | null;
    party?: string | null;
    chamber?: string | null;
    district?: string | null;
  }>;
  lobbyists: Array<{
    id: string;
    lobbyist_name?: string | null;
    lobbyist_firm_name?: string | null;
    client_name?: string | null;
    position?: string | null;
  }>;
  aiSummary?: {
    short_summary?: string | null;
    long_summary?: string | null;
    pro_position_summary?: string | null;
    con_position_summary?: string | null;
  } | null;
}

export default function BillScreen() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const workspaceId = useCitizenWorkspaceId();

  const { data, isLoading } = useQuery<BillDetail>(['citizen-bill', id], async () => {
    const { data } = await billsAPI.getById(id);
    return data.data;
  });

  const { data: tracking } = useQuery(
    ['citizen-bill-tracking', data?.bill?.id],
    async () => {
      const res = await billTrackingAPI.getTrackingForBill(data!.bill.id);
      return res.data.data as Array<{ workspace_id: string }>;
    },
    { enabled: !!data?.bill?.id }
  );

  const isTracked = !!tracking?.some((t) => t.workspace_id === workspaceId);

  async function toggleTrack() {
    if (!data?.bill?.id || !workspaceId) return;
    try {
      if (isTracked) {
        await billTrackingAPI.untrack(data.bill.id, [workspaceId]);
        toast.success('No longer tracking this issue');
      } else {
        await billTrackingAPI.track(data.bill.id, [workspaceId]);
        toast.success("We'll keep you posted on this issue");
      }
      queryClient.invalidateQueries(['citizen-bill-tracking', data.bill.id]);
    } catch {
      toast.error('Could not update tracking. Please try again.');
    }
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col screen-enter">
        <PageHeader title="Issue" onBack={() => navigate(-1)} />
        <p className="text-sm text-[color:var(--muted-foreground)] px-6 py-10">Loading…</p>
      </div>
    );
  }

  const b = data.bill;
  const stage = deriveStage(b);
  const summary = data.aiSummary?.long_summary || data.aiSummary?.short_summary || b.concerning || b.title;
  const pro = data.aiSummary?.pro_position_summary;
  const con = data.aiSummary?.con_position_summary;
  const primeSponsor = data.sponsors.find((s) => s.sponsor_type === 'Prime Sponsor') || data.sponsors[0];

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader
        title="Issue"
        onBack={() => navigate(-1)}
        right={
          <Button variant="ghost" size="icon" onClick={toggleTrack}>
            <Heart className="w-5 h-5" style={isTracked ? { fill: 'var(--primary)', color: 'var(--primary)' } : {}} />
          </Button>
        }
      />

      <div className="flex-1 pb-24">
        {/* Hero */}
        <div className="px-6 pt-4 pb-5">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge variant="default">{b.number}</Badge>
            {b.chamber && <Badge variant="secondary">{b.chamber}</Badge>}
            {(b.subject || []).slice(0, 2).map((s) => (
              <Badge key={s} variant="gold">
                {s}
              </Badge>
            ))}
          </div>
          <h1 className="text-[26px] font-extrabold tracking-tight leading-tight">{b.title}</h1>
          {b.introduced_date && (
            <p className="text-xs text-[color:var(--muted-foreground)] mt-2">
              Introduced {fmtDateShort(b.introduced_date)}
            </p>
          )}
        </div>

        {/* Progress timeline */}
        <div className="px-6 mb-5">
          <Card className="border-0 p-4">
            <SectionLabel>Where it stands</SectionLabel>
            <div className="relative pt-2">
              <div className="absolute left-3 right-3 top-[18px] h-0.5" style={{ background: 'var(--secondary)' }} />
              <div
                className="absolute left-3 top-[18px] h-0.5 transition-all"
                style={{
                  background: 'var(--primary)',
                  width: `calc(${(stage / (STAGES.length - 1)) * 100}% - ${stage === STAGES.length - 1 ? 24 : 0}px)`,
                }}
              />
              <div className="relative flex justify-between">
                {STAGES.map((s, i) => {
                  const past = i < stage;
                  const now = i === stage;
                  return (
                    <div key={s} className="flex flex-col items-center gap-1.5">
                      <div
                        className="relative w-9 h-9 rounded-full flex items-center justify-center border-2"
                        style={
                          now || past
                            ? { background: 'var(--primary)', borderColor: 'var(--primary)' }
                            : { background: 'var(--background)', borderColor: 'var(--border)' }
                        }
                      >
                        {past && <Check className="w-4 h-4 text-white" />}
                        {now && (
                          <>
                            <span
                              className="absolute inset-0 rounded-full anim-ping"
                              style={{ background: 'var(--primary)', opacity: 0.3 }}
                            />
                            <span className="relative w-3 h-3 rounded-full bg-white" />
                          </>
                        )}
                        {!past && !now && <span className="w-2 h-2 rounded-full" style={{ background: 'var(--border)' }} />}
                      </div>
                      <span
                        className="text-[10px] font-semibold tracking-wide"
                        style={now ? { color: 'var(--primary)' } : { color: 'var(--muted-foreground)' }}
                      >
                        {s}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {(b.latest_action || b.status) && (
              <p className="text-xs mt-4 px-1 leading-relaxed">
                <b style={{ color: 'var(--primary)' }}>Currently:</b>{' '}
                <span className="text-[color:var(--muted-foreground)]">{b.latest_action || b.status}</span>
              </p>
            )}
          </Card>
        </div>

        {/* Plain-language summary */}
        <div className="px-6 mb-5">
          <SectionLabel>What it does</SectionLabel>
          <p className="text-[15px] leading-relaxed text-[color:var(--foreground)] whitespace-pre-line">{summary}</p>
        </div>

        {/* Both sides — AI pro/con summaries */}
        {(pro || con) && (
          <div className="px-6 mb-5">
            <SectionLabel>Both sides</SectionLabel>
            <div className="space-y-2">
              {pro && (
                <Card className="p-4 border-0">
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: '#5A8F5A' }}>
                    Supporters say
                  </p>
                  <p className="text-sm leading-relaxed text-[color:var(--foreground)]">{pro}</p>
                </Card>
              )}
              {con && (
                <Card className="p-4 border-0">
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: '#C97B5A' }}>
                    Opponents say
                  </p>
                  <p className="text-sm leading-relaxed text-[color:var(--foreground)]">{con}</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Sponsors */}
        {data.sponsors.length > 0 && (
          <div className="px-6 mb-5">
            <SectionLabel>Sponsors</SectionLabel>
            <Card className="divide-y border-0 overflow-hidden">
              {data.sponsors.slice(0, 6).map((s, i) => {
                const display = flipName(s.name);
                return (
                  <div key={i} className="w-full p-4 flex items-center gap-3 text-left">
                    <Avatar initials={initialsOf(display)} size={40} tone="accent" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold">{display}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {s.party && (
                          <span className="inline-flex items-center gap-1 text-xs text-[color:var(--muted-foreground)]">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: partyColor(s.party) }} />
                            {formatPartyDisplay(s.party)}
                          </span>
                        )}
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)]">
                          · {s.sponsor_type || 'Sponsor'}
                          {s.district ? ` · District ${s.district}` : ''}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)]" />
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {/* Active lobbyists */}
        {data.lobbyists.length > 0 && (
          <div className="px-6 mb-5">
            <SectionLabel>Active lobbyists</SectionLabel>
            <div className="space-y-2">
              {data.lobbyists.slice(0, 5).map((l) => (
                <Card key={l.id} className="p-4 border-0 flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                  >
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">{l.lobbyist_name || l.lobbyist_firm_name}</p>
                    <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">
                      {l.client_name && <>For {l.client_name} · </>}
                      Position:{' '}
                      <span
                        className="font-semibold"
                        style={{
                          color:
                            (l.position || '').toLowerCase() === 'supporting'
                              ? '#5A8F5A'
                              : (l.position || '').toLowerCase() === 'opposing'
                                ? '#C97B5A'
                                : 'var(--primary)',
                        }}
                      >
                        {l.position || 'Monitoring'}
                      </span>
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className="px-6">
          <Button className="w-full" size="lg" onClick={toggleTrack}>
            <Heart className="w-4 h-4" style={isTracked ? { fill: 'currentColor' } : {}} />
            {isTracked ? 'Tracking this issue' : 'Track this issue'}
          </Button>
          {primeSponsor?.legislator_id && (
            <button
              onClick={() => navigate(`/citizen/legislator/${primeSponsor.legislator_id}`)}
              className="w-full text-center text-sm font-semibold py-3 mt-2 hover-elevate rounded-md"
              style={{ color: 'var(--primary)' }}
            >
              View prime sponsor →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
